/* One review owner for every native inscription form. Customer values are never normalised. */
(() => {
  'use strict';
  // Shopify's Liquid JSON view of line properties contains one HTML-entity layer.
  // Decode entity tokens only: preserve literal entities, spaces and CR/LF exactly.
  const entityDecoder = document.createElement('textarea');
  const propertyText = value => typeof value !== 'string' ? value : value.replace(/&(?:#[0-9]+|#x[0-9a-f]+|[a-z][a-z0-9]+);/giu, entity => {
    entityDecoder.innerHTML = entity;
    return entityDecoder.textContent;
  });
  const readProperties = serialized => {
    const raw = JSON.parse(serialized);
    const entries = Array.isArray(raw) ? raw : Object.entries(raw);
    return Object.fromEntries(entries.map(([key, value]) => [propertyText(key), propertyText(value)]));
  };
  const formState = new WeakMap();
  const syncPurchase = controls => {
    if (!controls?.hasAttribute('data-tf-purchase-ready')) return;
    const region = controls.querySelector('[data-tf-purchase-action]');
    const placeholder = region?.querySelector('[data-tf-purchase-placeholder]');
    const help = region?.querySelector('[data-tf-purchase-help]');
    if (!region || !placeholder || !help) return;
    const active = controls.querySelector('.tf-config-mode[open] [data-tf-engraving-form]');
    for (const form of controls.querySelectorAll('[data-tf-engraving-form]')) {
      const state = formState.get(form);
      if (!state) continue;
      if (form === active) {
        state.add.setAttribute('form', form.getAttribute('id'));
        region.insertBefore(state.add, placeholder);
      } else if (state.add.parentElement !== state.actionSlot) {
        state.actionSlot.append(state.add);
        state.add.removeAttribute('form');
      }
    }
    const state = active && formState.get(active);
    placeholder.hidden = Boolean(state);
    help.textContent = state ? (state.add.disabled && state.available ? controls.dataset.tfReviewHelp : '') : controls.dataset.tfChooseHelp;
    help.hidden = !help.textContent;
  };
  const snapshot = form => JSON.stringify(Array.from(form.querySelectorAll('[data-tf-field],[data-tf-quantity]'), field => [field.name, field.value]));
  const invalidate = form => {
    const state = formState.get(form);
    if (!state) return;
    state.reviewed = null;
    state.confirm.checked = false;
    state.confirm.disabled = true;
    state.add.disabled = true;
    state.lines.hidden = true;
    state.error.hidden = true;
    syncPurchase(form.closest('[data-tf-purchase-controls]'));
  };
  const addEntry = (list, label, value) => {
    const dt = document.createElement('dt');
    const dd = document.createElement('dd');
    dt.textContent = label;
    dd.textContent = value;
    list.append(dt, dd);
  };
  const frontFields = form => Array.from(form.querySelectorAll('[data-tf-kind="front-line"]'));
  const frontText = form => {
    const numbered = frontFields(form);
    if (numbered.length) {
      let last = numbered.length - 1;
      while (last >= 0 && numbered[last].value === '') last -= 1;
      return last < 0 ? '' : numbered.slice(0, last + 1).map(field => field.value).join('\n');
    }
    return form.querySelector('[data-tf-kind="front"]')?.value ?? null;
  };
  const syncLineCount = form => {
    const counter = form.querySelector('[data-tf-line-count]');
    if (!counter) return;
    const raw = frontText(form);
    const count = raw ? window.tfInscriptionRules?.inspect(raw).lineCount || 1 : 0;
    counter.textContent = (form.dataset.tfOnlineCount || '{count}/7').replace('{count}', String(count));
  };
  const syncFrontValidity = (form, rejected = false) => {
    const field = form.querySelector('[data-tf-kind="front"]');
    const region = form.querySelector('[data-tf-front-error]');
    if (!field || !region || !window.tfInscriptionRules) return;
    const result = window.tfInscriptionRules.inspect(field.value);
    const invalid = result.tooMany ? form.dataset.tfOnlineLineError : result.tooLong ? (form.dataset.tfOnlineWidthError || '').replace('{line}', String(result.tooLong)) : '';
    field.setCustomValidity(invalid);
    if (invalid) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
    const message = invalid || (rejected ? form.dataset.tfOnlineLineError : '');
    region.querySelector('[data-tf-status-message]').textContent = message;
    region.hidden = !message;
  };
  const validate = form => {
    syncFrontValidity(form);
    for (const field of form.querySelectorAll('[data-tf-field]')) {
      field.setCustomValidity('');
      field.removeAttribute('aria-invalid');
      if (field.required && !/\S/u.test(field.value)) field.setCustomValidity(form.dataset.tfRequiredError);
      if (!field.checkValidity()) {
        field.setAttribute('aria-invalid', 'true');
        field.reportValidity();
        return false;
      }
    }
    const raw = frontText(form);
    if (raw !== null && window.tfInscriptionRules) {
      const result = window.tfInscriptionRules.inspect(raw);
      if (!result.valid) {
        const numbered = frontFields(form);
        const target = result.tooLong && numbered.length ? numbered[result.tooLong - 1] : (form.querySelector('[data-tf-kind="front"]') || numbered[numbered.length - 1]);
        const message = result.tooMany ? form.dataset.tfOnlineLineError : (form.dataset.tfOnlineWidthError || '').replace('{line}', String(result.tooLong));
        if (target) {
          target.setCustomValidity(message);
          target.setAttribute('aria-invalid', 'true');
          target.reportValidity();
        }
        return false;
      }
    }
    const quantity = form.querySelector('[data-tf-quantity]');
    if (quantity && !quantity.reportValidity()) return false;
    return true;
  };
  const review = form => {
    const state = formState.get(form);
    if (!state || !validate(form)) return;
    state.lines.replaceChildren();
    addEntry(state.lines, form.dataset.tfMotifLabel, form.dataset.tfMotif);
    const numbered = Array.from(form.querySelectorAll('[data-tf-kind="front-line"]'));
    if (numbered.length) {
      // Seven positions remain seven positions, including deliberately empty intermediate lines.
      addEntry(state.lines, form.dataset.tfFrontLabel, numbered.map(field => field.value).join('\n'));
    } else if (form.dataset.tfMode === 'blank') {
      addEntry(state.lines, form.dataset.tfFrontLabel, form.dataset.tfBlankLabel);
    }
    for (const field of form.querySelectorAll('[data-tf-field]:not([data-tf-kind="front-line"])')) {
      addEntry(state.lines, field.dataset.tfLabel, field.value);
    }
    const quantity = form.querySelector('[data-tf-quantity]');
    if (quantity) addEntry(state.lines, form.dataset.tfQuantityLabel, quantity.value);
    state.reviewed = snapshot(form);
    state.lines.hidden = false;
    state.confirm.disabled = false;
    state.confirm.checked = false;
    state.add.disabled = true;
    state.error.hidden = true;
    state.lines.closest('[data-tf-review]').scrollIntoView({ block: 'nearest', behavior: 'auto' });
    state.confirm.focus({ preventScroll: true });
    syncPurchase(form.closest('[data-tf-purchase-controls]'));
  };
  let cartBusy = false;
  const saveCartLine = async form => {
    const state = formState.get(form);
    if (cartBusy) return;
    if (window.tfOrderLimits && !(await window.tfOrderLimits.check(form))) return;
    if (cartBusy) return;
    const dialog = form.closest('[data-tf-cart-properties]');
    const properties = readProperties(dialog.dataset.tfCartProperties);
    const frontKey = properties._tf_front_key;
    if (frontKey) {
      delete properties[frontKey];
      for (let line = 1; line <= 7; line += 1) delete properties[`${frontKey} / ${line}`];
    }
    const data = new FormData(form);
    for (const [name, value] of data) {
      const match = /^properties\[(.+)\]$/u.exec(name);
      if (match) properties[match[1]] = value;
    }
    cartBusy = true;
    const checkoutButtons = Array.from(document.querySelectorAll('[name="checkout"]'), button => [button, button.disabled]);
    checkoutButtons.forEach(([button]) => { button.disabled = true; });
    const submitButtons = Array.from(document.querySelectorAll('[data-tf-cart-key] [data-tf-add]'), button => [button, button.disabled]);
    submitButtons.forEach(([button]) => { button.disabled = true; });
    const mutationRegions = Array.from(document.querySelectorAll('[data-tf-native-cart],[data-tf-cart-key]'), region => [region, region.inert]);
    mutationRegions.forEach(([region]) => { region.inert = true; });
    const status = dialog.querySelector('[data-tf-cart-status]');
    if (status) status.hidden = false;
    form.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(dialog.dataset.tfCartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: form.dataset.tfCartKey, quantity: Number(data.get('quantity')), properties })
      });
      if (!response.ok) throw new Error('Cart change did not complete');
      await response.json();
      // Properties change the native line key; reloading binds every control to fresh server data.
      window.location.reload();
    } catch {
      mutationRegions.forEach(([region, inert]) => { region.inert = inert; });
      if (status) status.hidden = true;
      state.errorMessage.textContent = form.dataset.tfCartError;
      state.error.hidden = false;
      state.error.tabIndex = -1;
      state.error.focus();
      checkoutButtons.forEach(([button, disabled]) => { button.disabled = disabled; });
      submitButtons.forEach(([button, disabled]) => { button.disabled = disabled; });
      form.removeAttribute('aria-busy');
      cartBusy = false;
    }
  };
  const initialise = scope => {
    for (const form of scope.querySelectorAll('[data-tf-engraving-form]')) {
      if (formState.has(form)) continue;
      const confirm = form.querySelector('[data-tf-confirm]');
      const add = form.querySelector('[data-tf-add]');
      const lines = form.querySelector('[data-tf-review-lines]');
      const error = form.querySelector('[data-tf-form-error]');
      const button = form.querySelector('[data-tf-review-button]');
      if (!confirm || !add || !lines || !button || !error) continue;
      let errorMessage = error.querySelector('[data-tf-status-message]');
      if (!errorMessage) {
        errorMessage = document.createElement('span');
        errorMessage.dataset.tfStatusMessage = '';
        for (const node of Array.from(error.childNodes)) {
          if (node.nodeType === 3) errorMessage.append(node);
        }
        error.append(errorMessage);
      }
      formState.set(form, { confirm, add, lines, error, errorMessage, actionSlot: add.parentElement, reviewed: null, available: !add.disabled });
      const front = form.querySelector('[data-tf-kind="front"]');
      if (front) window.tfInscriptionRules?.bindInput(front, () => {
        invalidate(form);
        syncLineCount(form);
        syncFrontValidity(form, true);
      });
      syncLineCount(form);
      syncFrontValidity(form);
      document.fonts?.ready?.then(() => { if (form.isConnected) syncFrontValidity(form); });
      button.hidden = false;
      const editButton = scope.querySelector(`[data-tf-dialog-open="${form.closest('dialog')?.id || ''}"]`);
      if (editButton) editButton.hidden = false;
      invalidate(form);
    }
    for (const controls of scope.querySelectorAll('[data-tf-purchase-controls]')) {
      if (!Array.from(controls.querySelectorAll('[data-tf-engraving-form]')).every(form => formState.has(form))) continue;
      controls.setAttribute('data-tf-purchase-ready', '');
      syncPurchase(controls);
    }
  };
  initialise(document);
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-tf-review-button]');
    if (button) review(button.closest('[data-tf-engraving-form]'));
  });
  document.addEventListener('paste', event => {
    if (!event.target.matches('[data-tf-kind="front-line"]')) return;
    const text = event.clipboardData?.getData('text');
    if (!text || !/\r|\n/u.test(text)) return;
    event.preventDefault();
    const form = event.target.closest('[data-tf-engraving-form]');
    const fields = Array.from(form.querySelectorAll('[data-tf-kind="front-line"]'));
    const start = fields.indexOf(event.target);
    const lines = text.split(/\r\n|\r|\n/u);
    invalidate(form);
    if (start + lines.length > fields.length) {
      const state = formState.get(form);
      state.errorMessage.textContent = form.dataset.tfSevenLineError;
      state.error.hidden = false;
      state.error.tabIndex = -1;
      state.error.focus();
      return;
    }
    if (fields.slice(start + 1, start + lines.length).some(field => field.value !== '')) {
      const state = formState.get(form);
      state.errorMessage.textContent = form.dataset.tfPasteOccupied;
      state.error.hidden = false;
      state.error.tabIndex = -1;
      state.error.focus();
      return;
    }
    const prefix = event.target.value.slice(0, event.target.selectionStart);
    const suffix = event.target.value.slice(event.target.selectionEnd);
    lines.forEach((line, index) => { fields[start + index].value = (index === 0 ? prefix : '') + line + (index === lines.length - 1 ? suffix : ''); });
    syncLineCount(form);
    const last = fields[start + lines.length - 1];
    last.focus();
    last.setSelectionRange(last.value.length - suffix.length, last.value.length - suffix.length);
  });
  document.addEventListener('input', event => {
    const form = event.target.closest('[data-tf-engraving-form]');
    if (!form) return;
    const state = formState.get(form);
    if (!state) return;
    if (event.target === state.confirm) {
      state.add.disabled = !(state.available && !form.hasAttribute('data-tf-order-full') && state.confirm.checked && state.reviewed === snapshot(form));
      syncPurchase(form.closest('[data-tf-purchase-controls]'));
      return;
    }
    if (event.target.matches('[data-tf-field],[data-tf-quantity]')) {
      event.target.setCustomValidity('');
      event.target.removeAttribute('aria-invalid');
      invalidate(form);
      syncLineCount(form);
      syncFrontValidity(form);
    }
  });
  document.addEventListener('toggle', event => {
    if (!event.target.matches('.tf-config-mode')) return;
    if (event.target.open) {
      // Keep native details grouping consistent in browsers without name support.
      const name = event.target.getAttribute('name');
      if (name) document.querySelectorAll('.tf-config-mode[open]').forEach(details => {
        if (details !== event.target && details.getAttribute('name') === name) details.open = false;
      });
    } else {
      const form = event.target.querySelector('[data-tf-engraving-form]');
      if (form) invalidate(form);
    }
    syncPurchase(event.target.closest('[data-tf-purchase-controls]'));
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-tf-engraving-form]');
    if (!form) return;
    const state = formState.get(form);
    if (!state || !validate(form) || !state.confirm.checked || state.reviewed !== snapshot(form)) {
      event.preventDefault();
      if (state) {
        state.errorMessage.textContent = form.dataset.tfReviewError;
        state.error.hidden = false;
        state.confirm.checked = false;
        state.add.disabled = true;
        form.querySelector('[data-tf-review-button]').focus();
      }
    } else if (form.dataset.tfCartKey) {
      event.preventDefault();
      saveCartLine(form);
    }
  });

  /* Keep saved inscription values, but use the checkout language for their property labels. */
  const form = document.querySelector('#TfCart[data-tf-native-cart]');
  if (!form) return;
  const status = form.querySelector('[data-tf-cart-language-status]');
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const signature = properties => JSON.stringify(Object.entries(properties).sort(([a], [b]) => a.localeCompare(b)));
  const translated = line => {
    const original = readProperties(line.dataset.tfCartLineProperties);
    if (!original._tf_pattern || original._tf_pattern === 'chain') return null;
    const properties = { ...original };
    const move = (metadataKey, targetKey, nextValue) => {
      const oldKey = properties[metadataKey];
      if (!oldKey || !targetKey || !own(properties, oldKey)) return;
      const value = nextValue === undefined ? properties[oldKey] : nextValue;
      if (oldKey !== targetKey) {
        if (own(properties, targetKey) && properties[targetKey] !== value) throw new Error('Conflicting cart property');
        delete properties[oldKey];
      }
      properties[targetKey] = value;
      properties[metadataKey] = targetKey;
    };
    const frontKey = properties._tf_front_key;
    const nextFrontKey = form.dataset.tfFrontLabel;
    if (frontKey && nextFrontKey && frontKey !== nextFrontKey) {
      for (let number = 1; number <= 7; number += 1) {
        const oldKey = `${frontKey} / ${number}`;
        const nextKey = `${nextFrontKey} / ${number}`;
        if (!own(properties, oldKey)) continue;
        if (own(properties, nextKey) && properties[nextKey] !== properties[oldKey]) throw new Error('Conflicting inscription line');
        properties[nextKey] = properties[oldKey];
        delete properties[oldKey];
      }
    }
    move('_tf_motif_key', form.dataset.tfMotifLabel, line.dataset.tfCartLineTitle);
    move('_tf_front_key', nextFrontKey, properties._tf_front === 'blank' ? form.dataset.tfBlankLabel : undefined);
    move('_tf_date_key', form.dataset.tfDateLabel);
    move('_tf_initial_1_key', form.dataset.tfInitialOneLabel);
    move('_tf_initial_2_key', form.dataset.tfInitialTwoLabel);
    move('_tf_first_name_key', form.dataset.tfFirstNameLabel);
    if (signature(properties) === signature(original)) return null;
    return {
      key: line.dataset.tfCartLineKey,
      variant: Number(line.dataset.tfCartLineVariant),
      properties
    };
  };
  let busy = false;
  let ready = false;
  form.addEventListener('submit', async event => {
    if (event.submitter?.name !== 'checkout' || ready) return;
    if (busy) {
      event.preventDefault();
      return;
    }
    let changes;
    try {
      changes = Array.from(form.querySelectorAll('[data-tf-cart-line-key]'), translated).filter(Boolean);
    } catch {
      event.preventDefault();
      status.textContent = form.dataset.tfCartError;
      status.setAttribute('role', 'alert');
      status.hidden = false;
      return;
    }
    if (!changes.length) return;
    event.preventDefault();
    const button = event.submitter;
    busy = true;
    button.disabled = true;
    status.hidden = false;
    status.textContent = form.dataset.tfCartPending;
    status.setAttribute('role', 'status');
    try {
      if (window.tfOrderLimits && !(await window.tfOrderLimits.check(form))) {
        status.hidden = true;
        button.disabled = false;
        busy = false;
        return;
      }
      const state = await fetch(form.dataset.tfCartStateUrl, { headers: { Accept: 'application/json' } });
      if (!state.ok) throw new Error('Cart unavailable');
      let cart = await state.json();
      const originalCount = cart.item_count;
      const originalTotal = cart.total_price;
      for (const change of changes) {
        const line = cart.items.find(item => item.key === change.key);
        if (!line) {
          if (cart.items.some(item => item.variant_id === change.variant && signature(item.properties) === signature(change.properties))) continue;
          throw new Error('Cart line changed');
        }
        const response = await fetch(form.dataset.tfCartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: change.properties })
        });
        if (!response.ok) throw new Error('Cart translation failed');
        cart = await response.json();
        if (cart.item_count !== originalCount) throw new Error('Cart quantity changed');
      }
      if (cart.total_price !== originalTotal || changes.some(change => !cart.items.some(item => item.variant_id === change.variant && signature(item.properties) === signature(change.properties)))) throw new Error('Cart values changed');
      ready = true;
      status.hidden = true;
      button.disabled = false;
      if (window.tfOrderLimits) window.tfOrderLimits.submitApproved(form, button);
      else form.requestSubmit(button);
    } catch {
      status.textContent = form.dataset.tfCartError;
      status.setAttribute('role', 'alert');
      button.disabled = false;
      busy = false;
    }
  });
})();
